import { Instagram, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { LOGO_WORDMARK } from "../../data/promill";

const SDG_LOGO = "https://customer-assets.emergentagent.com/job_nourish-promill/artifacts/ap35tw1c_sdg%20logo.png";
const SDG_GOALS = [
    {
        id: 2,
        title: "Zero Hunger",
        image: "https://customer-assets.emergentagent.com/job_nourish-promill/artifacts/e3xyf2rc_goal2.JPG",
        url: "https://sdgs.un.org/goals/goal2",
    },
    {
        id: 3,
        title: "Good Health & Well-Being",
        image: "https://customer-assets.emergentagent.com/job_nourish-promill/artifacts/plgkpg7n_goal3.JPG",
        url: "https://sdgs.un.org/goals/goal3",
    },
    {
        id: 12,
        title: "Responsible Consumption & Production",
        image: "https://customer-assets.emergentagent.com/job_nourish-promill/artifacts/ee3p9t4w_goal12.JPG",
        url: "https://sdgs.un.org/goals/goal12",
    },
];

const COLS = [
    { title: "Explore", links: [
        { label: "Menu", to: "/menu" },
        { label: "Stores", to: "/stores" },
        { label: "The Food Promise", to: "/food-promise" },
        { label: "Nutrition", to: "/nutrition" },
    ]},
    { title: "Company", links: [
        { label: "About Us", to: "/about" },
        { label: "Franchise", to: "/franchise" },
    ]},
    { title: "Support", links: [
        { label: "FAQs", to: "/faqs" },
        { label: "Contact", to: "/contact" },
        { label: "Nutrition", to: "/nutrition" },
    ]},
    { title: "Legal", links: [
        { label: "Terms", to: "/legal/terms" },
        { label: "Privacy", to: "/legal/privacy" },
        { label: "Refund Policy", to: "/legal/refund" },
        { label: "Cookies", to: "/legal/cookies" },
    ]},
];

export const Footer = () => (
    <footer data-testid="site-footer" className="bg-pm-black text-pm-cream pt-20 pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-4">
                    <img src={LOGO_WORDMARK} alt="ProMill" className="h-8 w-auto" />
                    <p className="mt-5 text-pm-cream/70 max-w-sm leading-relaxed">
                        Protein-first. Millet-proud. Building the next generation of clean,
                        fast, Indian food — one bowl at a time.
                    </p>
                    <div className="mt-6 flex items-center gap-2 text-sm text-pm-cream/70">
                        <MapPin className="w-4 h-4 text-pm-yellow" />
                        <span>HQ: Bengaluru · Stores across India</span>
                    </div>
                    <a
                        href="https://www.instagram.com/promill__?igsh=MXhvaHUwMmd5ajJtaw%3D%3D"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="instagram-follow"
                        aria-label="Follow ProMill on Instagram"
                        className="mt-6 inline-flex items-center gap-3 px-4 py-2.5 rounded-full border border-pm-cream/20 hover:border-pm-yellow hover:bg-pm-yellow hover:text-pm-black transition-colors group"
                    >
                        <Instagram className="w-5 h-5" />
                        <span className="text-sm font-bold">Follow us on Instagram</span>
                    </a>

                    <a
                        href="https://theebg.com"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="ebg-group-card"
                        className="mt-5 inline-flex items-center gap-4 px-4 py-3 rounded-2xl border border-pm-cream/15 bg-black/40 hover:border-pm-yellow/40 transition-colors max-w-[300px]"
                    >
                        <img
                            src="https://customer-assets-eiarnc6j.emergentagent.net/job_nourish-promill/artifacts/6a4ytate_EBG%20Logo.png"
                            alt="EBG Group"
                            className="h-10 w-auto shrink-0"
                        />
                        <div className="min-w-0">
                            <div className="text-sm text-pm-cream leading-tight" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
                                An <span className="text-pm-yellow">EBG</span> Group Company
                            </div>
                            <div className="mt-0.5 text-xs text-pm-cream/50 flex items-center gap-1">
                                theebg.com <span aria-hidden>↗</span>
                            </div>
                        </div>
                    </a>
                </div>

                <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
                    {COLS.map((col) => (
                        <div key={col.title}>
                            <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-pm-yellow mb-4">
                                {col.title}
                            </h4>
                            <ul className="space-y-2.5">
                                {col.links.map((l) => (
                                    <li key={l.label}>
                                        <Link
                                            to={l.to}
                                            data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                            className="text-sm text-pm-cream/70 hover:text-pm-yellow transition-colors"
                                        >
                                            {l.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>

            {/* SDG alignment band */}
            <div
                data-testid="sdg-band"
                className="mt-16 pt-10 border-t border-pm-cream/15"
            >
                <div className="grid lg:grid-cols-2 gap-10 items-center">
                    {/* Left: logo + copy */}
                    <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
                        <img
                            src={SDG_LOGO}
                            alt="UN Sustainable Development Goals"
                            data-testid="sdg-main-logo"
                            className="h-14 md:h-16 w-auto mb-4"
                        />
                        <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-pm-yellow">
                            We are aligned with
                        </span>
                        <h4 className="mt-1.5 text-lg md:text-xl font-extrabold text-pm-cream leading-tight">
                            UN Sustainable Development Goals
                        </h4>
                        <p className="mt-2 text-sm text-pm-cream/60 leading-relaxed max-w-md">
                            Building food that supports better health, ends hunger, and respects our planet.
                        </p>
                    </div>

                    {/* Right: goal cards */}
                    <div className="flex gap-3 sm:gap-4 justify-center lg:justify-end flex-wrap">
                        {SDG_GOALS.map((g) => (
                            <a
                                key={g.id}
                                href={g.url}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={`UN SDG ${g.id}: ${g.title}`}
                                data-testid={`sdg-goal-${g.id}`}
                                title={`Goal ${g.id}: ${g.title}`}
                                className="group block"
                            >
                                <img
                                    src={g.image}
                                    alt={`SDG ${g.id} ${g.title}`}
                                    className="h-24 w-24 sm:h-28 sm:w-28 rounded-lg object-cover transition-transform group-hover:scale-105 ring-1 ring-white/10 group-hover:ring-pm-yellow"
                                />
                            </a>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-12 pt-8 border-t border-pm-cream/15 flex flex-col md:flex-row gap-4 items-center justify-between text-xs uppercase tracking-[0.18em] text-pm-cream/55 text-center md:text-left">
                <p>© {new Date().getFullYear()} ProMill Foods Pvt. Ltd. All rights reserved.</p>
                <p data-testid="reachvel-credit" className="normal-case tracking-normal text-pm-cream/70">
                    Design and Development by{" "}
                    <a
                        href="https://www.reachvel.com"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="reachvel-link"
                        className="font-bold text-pm-yellow hover:underline"
                    >
                        Reachvel
                    </a>
                </p>
            </div>
        </div>

        {/* Powered by band */}
        <div data-testid="powered-by-band" className="mt-10 bg-pm-cream text-pm-black/80 py-4 border-t border-pm-black/10">
            <div className="max-w-7xl mx-auto px-4 flex items-center justify-center text-xs tracking-wide">
                <span>
                    <span className="text-pm-black/50">Powered by </span>
                    <span className="font-bold text-pm-green">Native Touch</span>
                </span>
            </div>
        </div>
    </footer>
);
